import React from 'react';
import type { ThemePalette, Node as DiagramNode, Edge, SequenceParticipant, SequenceMessage, SequenceFragment, DiagramPage } from '../../types';
import { NODE_TYPES, NODE, STATUS_COLORS } from '../../constants';
import { CORE_DRAWING_SHAPE_TYPES, EXTRA_SHAPE_TYPES } from '../../constants/nodeTypes';
import { DIAGRAM_KIT_IDS, getKit, type DiagramKitId } from '../../utils/diagramKits';
import { GENERAL_SHAPES_SERVER_ENABLED } from '../../constants/featureFlags';
import type { CanvasDrawTool } from '../../stores/uiStore';
import { DraggableButton } from '../ui/DraggableButton';
import { FragmentPanelButton } from '../ui/FragmentPanelButton';
import { edgeService } from '../../services';
import { useDiagramStore } from '../../stores/diagramStore';
import { usedViaPointer, usedViaPointerNodeType } from '../../onboarding';
import { useUIStore } from '../../stores/uiStore';
import { armModeLabel } from '../../utils/drawTools';
import { innermostContainingFragment, nearestParticipantIds } from '../../utils/fragmentLayout';

const NODE_WIDTH = NODE.DEFAULT_WIDTH;

interface LeftSidebarProps {
  isViewOnly: boolean;
  palette: ThemePalette;
  glassStyle: React.CSSProperties;
  diagramType: 'flow' | 'sequence';
  
  sidebarTab: 'palette' | 'info' | 'pages';
  setSidebarTab: (tab: 'palette' | 'info' | 'pages') => void;
  setIsToolboxOpen: (v: boolean) => void;
  
  autoOpenInfoOnHover: boolean;
  hoveredNodeId: string | null;
  hoveredParticipantId: string | null;
  hoveredMessageId: string | null;
  
  selectedItems: string[];
  viewport: { x: number, y: number, scale: number };
  
  nodes: DiagramNode[];
  edges: Edge[];
  participants: SequenceParticipant[];
  messages: SequenceMessage[];
  fragments: SequenceFragment[];
  pages: DiagramPage[];
  activePage: string;
  
  snapshotBeforeMutation: () => void;
  addParticipant: (p: Pick<SequenceParticipant, 'label'> & Partial<Omit<SequenceParticipant, 'id' | 'order' | 'x' | 'label'>>) => void;
  groupParticipants: (ids: string[]) => void;
  addFragment: (f: Omit<SequenceFragment, 'id'>) => void;
  handleDragFromPanel: (nodeType: string, position: { x: number; y: number; }) => void;
  
  isFrameDrawMode: boolean;
  setIsFrameDrawMode: (v: boolean | ((prev: boolean) => boolean)) => void;
  frameDrawStart: React.MutableRefObject<{x: number, y: number} | null>;
  setFrameDrawRect: (r: { x: number; y: number; w: number; h: number } | null) => void;
  handleBeautifyLayout: () => void;
  bKeyHeld: React.MutableRefObject<boolean>;
  generalShapesEnabled?: boolean;
  canvasDrawTool?: CanvasDrawTool;
  setCanvasDrawTool?: (tool: CanvasDrawTool | ((prev: CanvasDrawTool) => CanvasDrawTool)) => void;
  canvasStampType?: string | null;
  setCanvasStampType?: (type: string | null) => void;
  kitId?: DiagramKitId;
  onKitChange?: (kit: DiagramKitId) => void;
  
  updateEdge: (id: string, update: Partial<Edge>) => void;
  removeEdge: (id: string) => void;
  
  setInfoPanelNodeId: (id: string | null) => void;
  setInfoPanelParticipantId: (id: string | null) => void;
  setInfoPanelMessageId: (id: string | null) => void;
  
  switchPageWithHistory: (id: string) => void;
  renamingPageId: string | null;
  setRenamingPageId: (id: string | null) => void;
  renamingPageVal: string;
  setRenamingPageVal: (v: string) => void;
  handleDeletePage: (id: string) => void;
}

export function LeftSidebar({
  isViewOnly, palette, glassStyle, diagramType,
  sidebarTab, setSidebarTab, setIsToolboxOpen,
  autoOpenInfoOnHover, hoveredNodeId, hoveredParticipantId, hoveredMessageId,
  selectedItems, viewport,
  nodes, edges, participants, messages, fragments, pages, activePage,
  snapshotBeforeMutation, addParticipant, groupParticipants, addFragment, handleDragFromPanel,
  isFrameDrawMode, frameDrawStart, setFrameDrawRect, handleBeautifyLayout, bKeyHeld, generalShapesEnabled,
  canvasDrawTool = 'none',
  canvasStampType = null,
  kitId = 'architecture', onKitChange,
  updateEdge, removeEdge,
  setInfoPanelNodeId, setInfoPanelParticipantId, setInfoPanelMessageId,
  switchPageWithHistory, renamingPageId, setRenamingPageId, renamingPageVal, setRenamingPageVal, handleDeletePage
}: LeftSidebarProps) {
  const tabs = (isViewOnly ? (['info', 'pages'] as const) : (['palette', 'info', 'pages'] as const));
  const baseTab = (tabs as ReadonlyArray<string>).includes(sidebarTab) ? sidebarTab : tabs[0];
  const isHoverInfoActive = autoOpenInfoOnHover && !!(hoveredNodeId || hoveredParticipantId || hoveredMessageId);
  const activeTab = !isViewOnly && isHoverInfoActive ? 'info' : baseTab;
  const showPalettePanel = !isViewOnly && activeTab === 'palette';
  const showInfoPanel = isViewOnly || activeTab === 'info';
  const showPagesPanel = isViewOnly || activeTab === 'pages';
  const canvasToolSticky = useUIStore(s => s.canvasToolSticky);
  const canvasConnectMode = useUIStore(s => s.canvasConnectMode);

  return (
    <div style={glassStyle} className="backdrop-blur-md border rounded-2xl shadow-2xl flex-1 min-h-0 flex flex-col overflow-hidden relative">
      {/* Tabbed header */}
      <div className="flex items-center justify-between px-2 py-2 flex-shrink-0 border-b" style={{ borderColor: palette.panelBorder }}>
        {!isViewOnly ? (
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === 'palette') usedViaPointer('sidebar-palette');
                  else if (tab === 'info') usedViaPointer('sidebar-info');
                  else usedViaPointer('sidebar-pages');
                  setSidebarTab(tab);
                }}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider transition-all ${
                  activeTab === tab ? 'bg-indigo-500/20 text-indigo-400' : 'hover:bg-white/5'
                }`}
                style={{ color: activeTab === tab ? '#818cf8' : palette.panelSubText }}
              >
                {tab}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: palette.panelSubText }}>
            Viewer
          </div>
        )}
        <button onClick={() => setIsToolboxOpen(false)} className="transition-colors p-0.5 rounded" style={{ color: palette.panelSubText }}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tab: Palette */}
      <div className={`tab-panel flex-1 min-h-0 flex flex-col overflow-hidden ${showPalettePanel ? 'tab-panel-active' : 'tab-panel-inactive'}`} data-tour="sidebar-palette">
        {diagramType === 'sequence' ? (
          <>
            <div className="flex-1 overflow-y-auto px-3 pb-3 pt-2 space-y-3">
              {!isViewOnly && (
                <button
                  onClick={() => {
                    usedViaPointer('add-participant');
                    snapshotBeforeMutation();
                    addParticipant({
                      label: `Participant ${participants.length + 1}`,
                    });
                  }}
                  className="w-full py-2 rounded-xl text-sm font-semibold transition-colors hover:bg-white/10"
                  style={{ color: palette.panelText, border: `1px solid ${palette.panelBorder}` }}
                >
                  + Add Participant
                </button>
              )}

              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider" style={{ color: palette.panelSubText }}>
                  Fragments
                </div>
                {!isViewOnly && (
                  <div className="grid grid-cols-2 gap-1.5">
                    {(['loop', 'alt', 'opt', 'par'] as const).map(type => (
                      <FragmentPanelButton
                        key={type}
                        type={type}
                        onDropOnCanvas={(fragmentType, screenX, screenY) => {
                          if (participants.length === 0) return;
                          const canvasX = (screenX - viewport.x) / viewport.scale;
                          const canvasY = (screenY - viewport.y) / viewport.scale;
                          const startY = Math.max(120, canvasY - 22);
                          const endY = startY + 90;
                          const selectedParticipantIds = selectedItems.filter((id) =>
                            participants.some((participant) => participant.id === id),
                          );
                          let participantIds = selectedParticipantIds.length >= 2
                            ? selectedParticipantIds
                            : nearestParticipantIds(participants, canvasX, 2);
                          if (participantIds.length === 0) {
                            participantIds = participants.map((participant) => participant.id);
                          }
                          const parent = innermostContainingFragment(fragments, startY, endY);
                          if (parent) {
                            const inherited = participantIds.filter((id) => parent.participantIds.includes(id));
                            participantIds = inherited.length > 0 ? inherited : [...parent.participantIds];
                          }
                          snapshotBeforeMutation();
                          addFragment({
                            type: fragmentType as 'loop' | 'alt' | 'opt' | 'par',
                            label: fragmentType === 'loop' ? '[condition]' : '',
                            participantIds,
                            startY,
                            endY,
                            parentFragmentId: parent?.id,
                            altDividerY: fragmentType === 'alt' ? startY + 45 : undefined,
                          });
                        }}
                      />
                    ))}
                  </div>
                )}
                {(participants.length === 0 || isViewOnly) && (
                  <div className="text-[10px]" style={{ color: palette.panelSubText }}>
                    {isViewOnly ? 'View-only mode: editing disabled.' : 'Add a participant before placing fragments.'}
                  </div>
                )}
              </div>

              {!isViewOnly && selectedItems.filter(id => participants.some(participant => participant.id === id)).length >= 2 && (
                <button
                  onClick={() => {
                    const selectedParticipantIds = selectedItems.filter(id => participants.some(participant => participant.id === id));
                    if (selectedParticipantIds.length < 2) return;
                    snapshotBeforeMutation();
                    groupParticipants(selectedParticipantIds);
                  }}
                  className="w-full py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-white/5"
                  style={{ color: palette.panelText, border: `1px solid ${palette.panelBorder}` }}
                >
                  Group Selected Participants
                </button>
              )}
            </div>
            <div className="px-4 py-2 border-t flex-shrink-0 space-y-2" style={{ borderColor: palette.panelBorder }}>
              <div className="text-[11px]" style={{ color: palette.panelSubText }}>
                Change the participant type from its details panel.
              </div>
              <div className="flex gap-3 text-xs" style={{ color: palette.panelSubText }}>
                <span>{participants.length} participants</span><span>·</span><span>{messages.length} messages</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-3 pb-3 pt-2">
              {!isViewOnly ? (() => {
                const kit = getKit(kitId);
                const showExtraShapes = kit.id === 'sketch' && GENERAL_SHAPES_SERVER_ENABLED && (generalShapesEnabled ?? false);
                const extraShapeEntries = Object.entries(NODE_TYPES).filter(([type]) => EXTRA_SHAPE_TYPES.has(type));
                const toggleDrawTool = (tool: CanvasDrawTool) => {
                  if (tool === 'box') usedViaPointer('draw-box');
                  if (tool === 'text') usedViaPointer('add-text');
                  if (tool === 'frame') usedViaPointer('draw-frame');
                  useUIStore.getState().cycleDrawTool(tool);
                };
                const stampType = (type: string) => {
                  usedViaPointerNodeType(type);
                  useUIStore.getState().cycleStampType(type);
                };
                const drawBtnStyle = (active: boolean, accent: string, sticky: boolean) => ({
                  color: active ? accent : '#c8d0e0',
                  background: active ? `${accent}22` : 'rgba(255,255,255,0.03)',
                  borderColor: active ? `${accent}80` : 'rgba(255,255,255,0.06)',
                  borderStyle: active && !sticky ? 'dashed' as const : 'solid' as const,
                });
                const modeSuffix = (active: boolean) => {
                  const mode = armModeLabel(active, canvasToolSticky);
                  return mode ? ` (${mode})` : '';
                };
                return (
                  <>
                    <div className="text-[10px] uppercase tracking-wider mb-1 px-1" style={{ color: palette.panelSubText }}>
                      Diagram kit
                    </div>
                    <div className="flex flex-wrap gap-1 mb-1.5">
                      {DIAGRAM_KIT_IDS.map(id => {
                        const item = getKit(id);
                        const on = kit.id === id;
                        return (
                          <button
                            key={id}
                            onClick={() => onKitChange?.(id)}
                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all border"
                            style={{
                              color: on ? '#a5b4fc' : palette.panelSubText,
                              background: on ? 'rgba(99,102,241,0.18)' : 'transparent',
                              borderColor: on ? '#6366f180' : 'rgba(255,255,255,0.06)',
                            }}
                            title={item.description}
                          >
                            {item.shortLabel}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[10px] leading-snug mb-2 px-1" style={{ color: palette.panelSubText }}>
                      {kit.description}
                    </p>
                    {kit.palette.map(group => (
                      <div key={group.title}>
                        {kit.palette.length > 1 || kit.id !== 'architecture' ? (
                          <div className="text-[10px] uppercase tracking-wider mb-1 px-1" style={{ color: palette.panelSubText }}>
                            {group.title}
                          </div>
                        ) : null}
                        <div className="grid grid-cols-2 gap-1">
                          {group.types.map(type => {
                            const meta = NODE_TYPES[type as keyof typeof NODE_TYPES];
                            if (!meta) return null;
                            if (type === 'box') {
                              const on = canvasDrawTool === 'box';
                              return (
                                <button
                                  key={type}
                                  onClick={() => toggleDrawTool('box')}
                                  className="flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] font-medium leading-tight transition-all border"
                                  style={drawBtnStyle(on, '#a5b4fc', canvasToolSticky)}
                                  title="Click once to draw one box, click again to keep drawing (d then b · 2)"
                                >
                                  ▭ Box{modeSuffix(on)}
                                </button>
                              );
                            }
                            return (
                              <DraggableButton key={type} nodeType={type} label={meta.label}
                                iconColor={meta.color} nodeWidth={NODE_WIDTH} nodeHeight={62}
                                onDragEnd={handleDragFromPanel}
                                onStamp={stampType}
                                stamped={canvasStampType === type}
                                stampSticky={canvasToolSticky}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <div className="text-[10px] uppercase tracking-wider mt-3 mb-1 px-1" style={{ color: palette.panelSubText }}>
                      Draw
                    </div>
                    <div className="grid grid-cols-2 gap-1" data-tour="draw-tools">
                      {kit.id !== 'sketch' && kit.drawTools.includes('box') && (
                        <button
                          onClick={() => toggleDrawTool('box')}
                          className="flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] font-medium leading-tight transition-all border"
                          style={drawBtnStyle(canvasDrawTool === 'box', '#a5b4fc', canvasToolSticky)}
                          title="Click once to draw one, click again to keep drawing (d then b · 2)"
                        >
                          ▭ Box{modeSuffix(canvasDrawTool === 'box')}
                        </button>
                      )}
                      <button
                        onClick={() => toggleDrawTool('text')}
                        className="flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] font-medium leading-tight transition-all border"
                        style={drawBtnStyle(canvasDrawTool === 'text', '#e2e8f0', canvasToolSticky)}
                        title="Click once to type one label, click again to keep placing (d then t)"
                      >
                        T Text{modeSuffix(canvasDrawTool === 'text')}
                      </button>
                      <DraggableButton nodeType="note" label={NODE_TYPES.note.label}
                        iconColor={NODE_TYPES.note.color} nodeWidth={NODE_WIDTH} nodeHeight={62}
                        onDragEnd={handleDragFromPanel}
                        onStamp={stampType}
                        stamped={canvasStampType === 'note'}
                        stampSticky={canvasToolSticky} />
                      <button
                        onClick={() => toggleDrawTool('arrow')}
                        className="flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] font-medium leading-tight transition-all border"
                        style={drawBtnStyle(canvasDrawTool === 'arrow', '#94a3b8', canvasToolSticky)}
                        title="Click once to draw one arrow, click again to keep drawing (d then a)"
                      >
                        → Arrow{modeSuffix(canvasDrawTool === 'arrow')}
                      </button>
                      <button
                        onClick={() => toggleDrawTool('line')}
                        className="flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] font-medium leading-tight transition-all border"
                        style={drawBtnStyle(canvasDrawTool === 'line', '#94a3b8', canvasToolSticky)}
                        title="Click once to draw one line, click again to keep drawing (d then l)"
                      >
                        ─ Line{modeSuffix(canvasDrawTool === 'line')}
                      </button>
                      <button
                        onClick={() => useUIStore.getState().cycleConnectMode()}
                        className="flex min-h-[32px] items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-[11px] font-medium leading-tight transition-all border"
                        style={drawBtnStyle(canvasConnectMode !== 'none', '#38bdf8', canvasConnectMode === 'sticky')}
                        title="Click two nodes to connect. Click again to keep chaining. Hold E and drag from a node body."
                      >
                        ⤷ Connect{canvasConnectMode === 'none' ? '' : canvasConnectMode === 'sticky' ? ' (sticky)' : ' (once)'}
                      </button>
                      {kit.id !== 'sketch' && [...CORE_DRAWING_SHAPE_TYPES].map(type => {
                        const meta = NODE_TYPES[type as keyof typeof NODE_TYPES];
                        if (!meta) return null;
                        return (
                          <DraggableButton key={type} nodeType={type} label={meta.label}
                            iconColor={meta.color} nodeWidth={NODE_WIDTH} nodeHeight={62}
                            onDragEnd={handleDragFromPanel}
                            onStamp={stampType}
                            stamped={canvasStampType === type}
                            stampSticky={canvasToolSticky} />
                        );
                      })}
                    </div>
                    {showExtraShapes && extraShapeEntries.length > 0 && (
                      <>
                        <div className="text-[10px] uppercase tracking-wider mt-3 mb-1 px-1" style={{ color: palette.panelSubText }}>
                          Extra shapes
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {extraShapeEntries.map(([type, meta]) => (
                              <DraggableButton key={type} nodeType={type} label={meta.label}
                                iconColor={meta.color} nodeWidth={NODE_WIDTH} nodeHeight={62}
                                onDragEnd={handleDragFromPanel}
                                onStamp={stampType}
                                stamped={canvasStampType === type}
                                stampSticky={canvasToolSticky}
                              />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })() : (
                <div className="text-xs py-2" style={{ color: palette.panelSubText }}>
                  View-only mode: nodes and edges cannot be edited.
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t flex-shrink-0 space-y-2" style={{ borderColor: palette.panelBorder }}>
              {!isViewOnly && (
                <>
                  <button
                    onClick={() => {
                      usedViaPointer('draw-frame');
                      bKeyHeld.current = false;
                      useUIStore.getState().cycleDrawTool('frame');
                      if (useUIStore.getState().canvasDrawTool !== 'frame') {
                        frameDrawStart.current = null;
                        setFrameDrawRect(null);
                      }
                    }}
                    className={`w-full py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                      canvasDrawTool === 'frame' || isFrameDrawMode
                        ? 'border-amber-400 text-amber-400 bg-amber-400/10'
                        : 'border-transparent hover:border-amber-400/50 hover:text-amber-400'
                    }`}
                    style={(canvasDrawTool === 'frame' || isFrameDrawMode) ? {
                      borderStyle: canvasToolSticky ? 'solid' : 'dashed',
                    } : { color: palette.panelSubText }}
                    title="Click once to draw one frame, click again to keep drawing (d then f · hold B)"
                    data-tour="draw-frame"
                  >
                    ▣ Draw Frame{canvasDrawTool === 'frame' || isFrameDrawMode
                      ? (canvasToolSticky ? ' (sticky)' : ' (once)')
                      : ''}
                  </button>
                  <button
                    onClick={handleBeautifyLayout}
                    disabled={nodes.length < 2}
                    className="w-full py-1.5 rounded-lg text-xs font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white' }}
                    title="Arrange nodes and route edges around them. Which way it ranks them follows Beautify Direction in Settings."
                  >
                    ✦ Beautify Layout
                  </button>
                </>
              )}
            <div className="flex gap-3 text-xs" style={{ color: palette.panelSubText }}>
              <span>{nodes.length} nodes</span><span>·</span><span>{edges.length} edges</span>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Tab: Info - shows hovered or selected node info */}
      <div className={`tab-panel min-h-0 flex flex-col overflow-hidden ${showInfoPanel ? 'tab-panel-active flex-1' : 'tab-panel-inactive'}`} data-tour="sidebar-info">{(() => {
        const selectedSingleId = selectedItems.length === 1 ? selectedItems[0] : null;
        const sidebarInfoTarget =
          hoveredNodeId ? { kind: 'node' as const, id: hoveredNodeId } :
          hoveredParticipantId ? { kind: 'participant' as const, id: hoveredParticipantId } :
          hoveredMessageId ? { kind: 'message' as const, id: hoveredMessageId } :
          selectedSingleId && nodes.some(n => n.id === selectedSingleId) ? { kind: 'node' as const, id: selectedSingleId } :
          selectedSingleId && participants.some(p => p.id === selectedSingleId) ? { kind: 'participant' as const, id: selectedSingleId } :
          selectedSingleId && messages.some(m => m.id === selectedSingleId) ? { kind: 'message' as const, id: selectedSingleId } :
          null;

        const infoTargetNode = sidebarInfoTarget?.kind === 'node'
          ? nodes.find(n => n.id === sidebarInfoTarget.id) ?? null
          : null;
        const infoTargetParticipant = sidebarInfoTarget?.kind === 'participant'
          ? participants.find(p => p.id === sidebarInfoTarget.id) ?? null
          : null;
        const infoTargetMessage = sidebarInfoTarget?.kind === 'message'
          ? messages.find(m => m.id === sidebarInfoTarget.id) ?? null
          : null;

        const selectedEdgeIds = !sidebarInfoTarget ? selectedItems.filter(id => edges.some(e => e.id === id)) : [];
        const selectedEdgeObjs = selectedEdgeIds.map(id => edges.find(e => e.id === id)!).filter(Boolean);
        const firstEdge = selectedEdgeObjs[0] ?? null;

        if (!isViewOnly && !infoTargetNode && selectedEdgeObjs.length > 0 && firstEdge) {
          const sw = firstEdge.strokeWidth ?? 1.5;
          const dt = firstEdge.dashStyle ?? 'solid';
          const et = firstEdge.edgeType ?? 'arrow';
          const STROKE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
          const thicknessIdx = STROKE_STEPS.indexOf(sw);
          return (
            <div className="flex-1 overflow-y-auto px-3 pb-3 pt-2 space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: palette.panelSubText }}>
                {selectedEdgeObjs.length === 1 ? 'Edge' : `${selectedEdgeObjs.length} Edges`}
              </div>

              {/* Edge Type */}
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Type <span className="normal-case opacity-60 ml-1">= to cycle</span></div>
                <div className="flex gap-1">
                  {([
                    { key: 'arrow', label: '→', title: 'Arrow (default)' },
                    { key: 'no-arrow', label: '—', title: 'No arrow (plain line)' },
                    { key: 'bidirectional', label: '↔', title: 'Bidirectional' },
                  ] as const).map(({ key, label, title }) => (
                    <button key={key} title={title}
                      className="flex-1 py-1 rounded text-xs font-bold transition-all"
                      style={{
                        background: et === key ? `${firstEdge.color ?? palette.edgeDefault}25` : 'transparent',
                        color: et === key ? (firstEdge.color ?? palette.edgeDefault) : palette.panelSubText,
                        border: `1px solid ${et === key ? (firstEdge.color ?? palette.edgeDefault) + '80' : palette.panelBorder}`,
                      }}
                      onClick={() => { snapshotBeforeMutation(); selectedEdgeIds.forEach(id => updateEdge(id, { edgeType: key })); }}
                    >{label}</button>
                  ))}
                </div>
                {(kitId === 'uml' || et === 'inheritance' || et === 'aggregation' || et === 'composition') && (
                  <div className="flex gap-1 mt-1">
                    {([
                      { key: 'inheritance', label: '△', title: 'Inheritance (generalization)' },
                      { key: 'aggregation', label: '◇', title: 'Aggregation' },
                      { key: 'composition', label: '◆', title: 'Composition' },
                    ] as const).map(({ key, label, title }) => (
                      <button key={key} title={title}
                        className="flex-1 py-1 rounded text-xs font-bold transition-all"
                        style={{
                          background: et === key ? `${firstEdge.color ?? palette.edgeDefault}25` : 'transparent',
                          color: et === key ? (firstEdge.color ?? palette.edgeDefault) : palette.panelSubText,
                          border: `1px solid ${et === key ? (firstEdge.color ?? palette.edgeDefault) + '80' : palette.panelBorder}`,
                        }}
                        onClick={() => { snapshotBeforeMutation(); selectedEdgeIds.forEach(id => updateEdge(id, { edgeType: key })); }}
                      >{label}</button>
                    ))}
                  </div>
                )}
              </div>

              {/* Dash Style */}
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: palette.panelSubText }}>Line <span className="normal-case opacity-60 ml-1">- to cycle</span></div>
                <div className="flex gap-1">
                  {([
                    { key: 'solid', label: '─', title: 'Solid' },
                    { key: 'dashed', label: '╌', title: 'Dashed' },
                    { key: 'dotted', label: '⋯', title: 'Dotted' },
                    { key: 'dash-dot', label: '─·', title: 'Dash-dot' },
                    { key: 'long-dash', label: '╍', title: 'Long dash' },
                  ] as const).map(({ key, label, title }) => (
                    <button key={key} title={title}
                      className="flex-1 py-1 rounded text-xs font-bold transition-all"
                      style={{
                        background: dt === key ? `${firstEdge.color ?? palette.edgeDefault}25` : 'transparent',
                        color: dt === key ? (firstEdge.color ?? palette.edgeDefault) : palette.panelSubText,
                        border: `1px solid ${dt === key ? (firstEdge.color ?? palette.edgeDefault) + '80' : palette.panelBorder}`,
                      }}
                      onClick={() => { snapshotBeforeMutation(); selectedEdgeIds.forEach(id => updateEdge(id, { dashStyle: key })); }}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {/* Thickness */}
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 flex items-center justify-between" style={{ color: palette.panelSubText }}>
                  <span>Thickness <span className="normal-case opacity-60 ml-1">⌘+/− keys</span></span>
                  <span className="text-[9px] font-mono" style={{ color: palette.panelText }}>{sw}px</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    className="w-6 h-6 rounded text-xs font-bold flex items-center justify-center transition-colors hover:bg-white/10"
                    style={{ color: palette.panelSubText, border: `1px solid ${palette.panelBorder}` }}
                    onClick={() => { snapshotBeforeMutation(); edgeService.decreaseThickness(selectedEdgeIds); }}
                  >−</button>
                  {/* Visual thickness bar */}
                  <div className="flex-1 flex items-center gap-0.5 h-6">
                    {STROKE_STEPS.map((s, i) => (
                      <button
                        key={s}
                        title={`${s}px`}
                        className="flex-1 rounded-sm transition-all hover:opacity-100"
                        style={{
                          height: Math.max(2, s * 1.2),
                          background: i <= thicknessIdx ? (firstEdge.color ?? palette.edgeDefault) : palette.panelBorder,
                          opacity: i <= thicknessIdx ? 1 : 0.3,
                        }}
                        onClick={() => { snapshotBeforeMutation(); selectedEdgeIds.forEach(id => updateEdge(id, { strokeWidth: s })); }}
                      />
                    ))}
                  </div>
                  <button
                    className="w-6 h-6 rounded text-xs font-bold flex items-center justify-center transition-colors hover:bg-white/10"
                    style={{ color: palette.panelSubText, border: `1px solid ${palette.panelBorder}` }}
                    onClick={() => { snapshotBeforeMutation(); edgeService.increaseThickness(selectedEdgeIds); }}
                  >+</button>
                </div>
              </div>

              {/* Animate toggle */}
              <button
                className="w-full py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5"
                style={{
                  color: firstEdge.animated ? '#818cf8' : palette.panelSubText,
                  borderColor: firstEdge.animated ? '#818cf850' : palette.panelBorder,
                  background: firstEdge.animated ? '#818cf810' : 'transparent',
                }}
                onClick={() => { snapshotBeforeMutation(); edgeService.toggleAnimation(selectedEdgeIds); }}
              >
                {firstEdge.animated ? '⚡ Animated' : '⚡ Animate'}
              </button>

              {/* Delete */}
              <button
                className="w-full py-1.5 rounded-lg text-xs font-medium border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors"
                onClick={() => { snapshotBeforeMutation(); selectedEdgeIds.forEach(id => removeEdge(id)); }}
              >
                Delete {selectedEdgeObjs.length > 1 ? `${selectedEdgeObjs.length} edges` : 'Edge'}
              </button>
            </div>
          );
        }
        return (
          <div className="flex-1 overflow-y-auto px-4 pb-3 pt-2">
            {infoTargetNode ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: infoTargetNode.data?.color ?? '#6366f1' }} />
                  <span className="text-sm font-semibold" style={{ color: palette.panelText }}>{infoTargetNode.label}</span>
                </div>
                <div className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded inline-block" style={{ background: (infoTargetNode.data?.color ?? '#6366f1') + '20', color: infoTargetNode.data?.color ?? '#6366f1' }}>
                  {infoTargetNode.type}
                </div>
                {infoTargetNode.meta?.description && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Description</div>
                    <p className="text-xs" style={{ color: palette.panelText }}>{infoTargetNode.meta.description}</p>
                  </div>
                )}
                {infoTargetNode.meta?.owner && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Owner</div>
                    <p className="text-xs" style={{ color: palette.panelText }}>{infoTargetNode.meta.owner}</p>
                  </div>
                )}
                {infoTargetNode.meta?.status && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Status</div>
                    <span className="text-xs capitalize px-1.5 py-0.5 rounded" style={{ background: (STATUS_COLORS[infoTargetNode.meta.status] ?? '#94a3b8') + '20', color: STATUS_COLORS[infoTargetNode.meta.status] ?? '#94a3b8' }}>
                      {infoTargetNode.meta.status}
                    </span>
                  </div>
                )}
                {(infoTargetNode.meta?.links ?? []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Links</div>
                    <div className="space-y-1">
                      {infoTargetNode.meta?.links?.map((link, i) => (
                        <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                          className="block text-xs text-indigo-400 hover:text-indigo-300 truncate">
                          ↗ {link.label || link.url}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {!isViewOnly && (
                  <button
                    onClick={() => setInfoPanelNodeId(infoTargetNode.id)}
                    className="w-full py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5"
                    style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                  >
                    Open Full Info Panel
                  </button>
                )}
              </div>
            ) : infoTargetParticipant && diagramType === 'sequence' ? (() => {
              const hp = infoTargetParticipant;
              if (!hp) return null;
              return (
                <div className="space-y-3 relative">
                  {hp.type && (
                    <span
                      className="absolute top-0 right-0 text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold"
                      style={{ background: hp.color + '25', color: hp.color, transform: 'translateY(-2px)' }}
                    >{hp.type}</span>
                  )}
                  <div className="flex items-center gap-2 pr-12">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: hp.color }} />
                    <span className="text-sm font-semibold leading-tight" style={{ color: palette.panelText }}>{hp.label}</span>
                  </div>
                  {hp.description && (
                    <p className="text-xs leading-relaxed" style={{ color: palette.panelSubText }}>{hp.description}</p>
                  )}
                  {!isViewOnly && (
                    <button
                      onClick={() => setInfoPanelParticipantId(hp.id)}
                      className="w-full py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5"
                      style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                    >Open Full Info Panel</button>
                  )}
                </div>
              );
            })() : infoTargetMessage && diagramType === 'sequence' ? (() => {
              const hoveredMessage = infoTargetMessage;
              if (!hoveredMessage) return null;
              const srcParticipant = participants.find(participant => participant.id === hoveredMessage.sourceId);
              const tgtParticipant = participants.find(participant => participant.id === hoveredMessage.targetId);
              const messageAccent = hoveredMessage.color ?? '#6366f1';
              return (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: messageAccent }}>
                      {hoveredMessage.type}
                    </div>
                    <div className="text-sm font-semibold leading-tight" style={{ color: palette.panelText }}>
                      {hoveredMessage.label || 'Untitled message'}
                    </div>
                  </div>
                  {hoveredMessage.description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Description</div>
                      <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: palette.panelText }}>{hoveredMessage.description}</div>
                    </div>
                  )}
                  {hoveredMessage.tooltip && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Tooltip</div>
                      <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: palette.panelText }}>{hoveredMessage.tooltip}</div>
                    </div>
                  )}
                  {hoveredMessage.payload && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Payload</div>
                      <pre className="text-xs whitespace-pre-wrap rounded-lg border px-2 py-1.5 overflow-x-auto" style={{ color: palette.panelText, borderColor: palette.panelBorder, background: palette.panelBg }}>
                        {hoveredMessage.payload}
                      </pre>
                    </div>
                  )}
                  {hoveredMessage.constraints && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Constraints</div>
                      <div className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: palette.panelText }}>{hoveredMessage.constraints}</div>
                    </div>
                  )}
                  {(hoveredMessage.links ?? []).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Links</div>
                      <div className="space-y-1">
                        {(hoveredMessage.links ?? []).map((link, index) => (
                          <a
                            key={`${hoveredMessage.id}-link-${index}`}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-indigo-400 hover:text-indigo-300 truncate"
                          >
                            {link.label || link.url}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {!hoveredMessage.description && !hoveredMessage.tooltip && !hoveredMessage.payload && !hoveredMessage.constraints && (hoveredMessage.links ?? []).length === 0 && (
                    <div className="text-xs leading-relaxed" style={{ color: palette.panelSubText }}>
                      No extra message details are set yet.
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: palette.panelSubText }}>Participants</div>
                    <div className="text-xs" style={{ color: palette.panelSubText }}>
                      {(srcParticipant?.label ?? '?') + ' -> ' + (tgtParticipant?.label ?? '?')}
                    </div>
                  </div>
                  {!isViewOnly && (
                    <button
                      onClick={() => setInfoPanelMessageId(hoveredMessage.id)}
                      className="w-full py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5"
                      style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
                    >Open Full Info Panel</button>
                  )}
                </div>
              );
            })() : (
              <div className="text-center py-8">
                <div className="text-2xl mb-2" style={{ color: palette.panelBorder }}>ℹ</div>
                <p className="text-xs" style={{ color: palette.panelSubText }}>
                  {diagramType === 'sequence' ? 'Hover a participant or message to see info here.' : 'Hover over a node to see its info here, or select a single node.'}
                </p>
              </div>
            )}
          </div>
        );
      })()}</div>

      {/* Tab: Pages */}
      <div className={`tab-panel min-h-0 flex flex-col overflow-hidden ${showPagesPanel ? 'tab-panel-active flex-1' : 'tab-panel-inactive'}`} data-tour="sidebar-pages">{(() => {
        const commitPageRename = () => {
          if (renamingPageId && renamingPageVal.trim()) useDiagramStore.getState().renamePage(renamingPageId, renamingPageVal.trim());
          setRenamingPageId(null); setRenamingPageVal('');
        };
        return (
          <div className="flex-1 overflow-y-auto px-4 pb-3 pt-2">
            <div className="space-y-1">
              {pages.map(page => (
                <div
                  key={page.id}
                  className={`group flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
                    page.id === activePage ? 'bg-indigo-500/15' : 'hover:bg-white/5'
                  }`}
                  style={{ borderLeft: page.id === activePage ? '3px solid #818cf8' : '3px solid transparent' }}
                  onClick={() => { if (renamingPageId !== page.id) switchPageWithHistory(page.id); }}
                >
                  <span className="text-xs flex-shrink-0" style={{ color: page.id === activePage ? '#818cf8' : palette.panelSubText }}>⊞</span>
                  {!isViewOnly && renamingPageId === page.id ? (
                    <input
                      autoFocus
                      value={renamingPageVal}
                      onChange={e => setRenamingPageVal(e.target.value)}
                      onBlur={commitPageRename}
                      onKeyDown={e => { if (e.key === 'Enter') commitPageRename(); if (e.key === 'Escape') { setRenamingPageId(null); setRenamingPageVal(''); } e.stopPropagation(); }}
                      onClick={e => e.stopPropagation()}
                      className="flex-1 bg-transparent border-b outline-none text-xs px-0"
                      style={{ color: palette.panelText, borderColor: '#818cf8' }}
                    />
                  ) : (
                    <span
                      className="text-xs truncate flex-1"
                      style={{ color: page.id === activePage ? palette.panelText : palette.panelSubText }}
                      onDoubleClick={e => {
                        if (isViewOnly) return;
                        e.stopPropagation();
                        setRenamingPageId(page.id);
                        setRenamingPageVal(page.name);
                      }}
                    >
                      {page.name}
                    </span>
                  )}
                  {!isViewOnly && renamingPageId !== page.id && (
                    <>
                      <button
                        className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded text-[9px] opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: palette.panelSubText }}
                        title="Rename"
                        onClick={e => { e.stopPropagation(); setRenamingPageId(page.id); setRenamingPageVal(page.name); }}
                      >✏</button>
                      <button
                        className="flex-shrink-0 px-1 h-4 rounded text-[8px] font-semibold"
                        style={{
                          background: (page.diagramType ?? 'flow') === 'sequence' ? '#0891b220' : '#4f46e520',
                          color: (page.diagramType ?? 'flow') === 'sequence' ? '#22d3ee' : '#818cf8',
                          border: `1px solid ${(page.diagramType ?? 'flow') === 'sequence' ? '#22d3ee40' : '#818cf840'}`,
                        }}
                        title="Click to switch diagram type"
                        onClick={e => { e.stopPropagation(); useDiagramStore.getState().setPageDiagramType(page.id, (page.diagramType ?? 'flow') === 'flow' ? 'sequence' : 'flow'); }}
                      >
                        {(page.diagramType ?? 'flow') === 'sequence' ? 'SEQ' : 'FLOW'}
                      </button>
                      {pages.length > 1 && (
                        <button
                          className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded text-[9px] opacity-0 group-hover:opacity-100 transition-colors hover:text-red-400"
                          style={{ color: palette.panelSubText }}
                          title="Delete page"
                          onClick={e => {
                            e.stopPropagation();
                            if (window.confirm(`Delete page "${page.name}"?`)) handleDeletePage(page.id);
                          }}
                        >
                          ×
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            {!isViewOnly && (
            <div className="flex gap-1.5 mt-3">
              <button
                onClick={() => { const newId = useDiagramStore.getState().addPage(undefined, 'flow'); switchPageWithHistory(newId); }}
                className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5"
                style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
              >+ Flow</button>
              <button
                onClick={() => { const newId = useDiagramStore.getState().addPage(undefined, 'sequence'); switchPageWithHistory(newId); }}
                className="flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors hover:bg-white/5"
                style={{ color: palette.panelSubText, borderColor: palette.panelBorder }}
              >+ Seq</button>
            </div>
            )}
            <p className="text-[9px] mt-2 text-center" style={{ color: palette.panelSubText, opacity: 0.6 }}>
              {isViewOnly ? 'Read-only page navigation' : 'Dbl-click to rename, Del deletes'}
            </p>
          </div>
        );
      })()}</div>

    </div>
  );
}
